"use client";

import Link from "next/link";
import { ArrowLeft, Accessibility, Eye, Mouse, Keyboard, Monitor, Phone, Mail, CheckCircle } from "lucide-react";
import { useSiteConfig } from '@/context/SiteConfigContext';

export default function AccessibilityClient() {
  const siteConfig = useSiteConfig();
  const companyName = siteConfig.companyName || 'Smart Blinds™';
  const domain = siteConfig.domain || 'smartblindshub.com';
  const accessibilityEmail = `accessibility@${domain}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[var(--primary-red)] text-white py-8 sm:py-12">
        <div className="container mx-auto px-3 sm:px-4">
          <Link href="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-3 sm:mb-4 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <Accessibility className="w-7 h-7 sm:w-10 sm:h-10" />
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">Accessibility Statement</h1>
          </div>
          <p className="text-white/90 max-w-2xl text-sm sm:text-base">
            {companyName} is committed to ensuring digital accessibility for people with disabilities.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Commitment */}
          <section className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-5 sm:p-8 mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Our Commitment</h2>
            <p className="text-gray-700 mb-4">
              {companyName} is committed to making our website accessible to all users, including those with disabilities.
              We strive to ensure that our website meets or exceeds the requirements of the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA.
            </p>
            <p className="text-gray-700">
              We continually work to improve the user experience for everyone and apply the relevant accessibility standards
              to achieve these goals. Our efforts are ongoing, and we welcome feedback on how we can improve.
            </p>
          </section>

          {/* Accessibility Features */}
          <section className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-5 sm:p-8 mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Accessibility Features</h2>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="flex gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Keyboard className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Keyboard Navigation</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    All interactive elements can be accessed using keyboard-only navigation.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Screen Reader Support</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    Our website is compatible with popular screen readers like JAWS, NVDA, and VoiceOver.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Monitor className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Text Resizing</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    Text can be resized up to 200% without loss of content or functionality.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mouse className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">Focus Indicators</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    Clear visual indicators show which element has keyboard focus.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* What We're Doing */}
          <section className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-5 sm:p-8 mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">What We're Doing</h2>
            <ul className="space-y-2 sm:space-y-3">
              {[
                "Providing text alternatives for non-text content",
                "Creating content that can be presented in different ways without losing information",
                "Making it easier for users to see and hear content",
                "Providing keyboard accessibility for all functionality",
                "Giving users enough time to read and use content",
                "Not designing content in a way that is known to cause seizures",
                "Providing ways to help users navigate and find content",
                "Making text content readable and understandable",
                "Making web pages appear and operate in predictable ways",
                "Helping users avoid and correct mistakes",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Browser & Assistive Technology */}
          <section className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-5 sm:p-8 mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Compatible Browsers & Assistive Technologies</h2>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Supported Browsers</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-gray-600 text-sm">
                  <li>• Google Chrome (latest 2 versions)</li>
                  <li>• Mozilla Firefox (latest 2 versions)</li>
                  <li>• Apple Safari (latest 2 versions)</li>
                  <li>• Microsoft Edge (latest 2 versions)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Assistive Technologies</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-gray-600 text-sm">
                  <li>• JAWS (Windows)</li>
                  <li>• NVDA (Windows)</li>
                  <li>• VoiceOver (macOS, iOS)</li>
                  <li>• TalkBack (Android)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Feedback */}
          <section className="bg-gray-800 rounded-lg sm:rounded-xl p-5 sm:p-8 text-white">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Accessibility Feedback</h2>
            <p className="text-white/80 mb-4 sm:mb-6 text-sm sm:text-base">
              We welcome your feedback on the accessibility of {companyName}. If you encounter any accessibility
              barriers or have suggestions for improvement, please let us know:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--primary-red)]" />
                <div>
                  <p className="text-xs sm:text-sm text-white/60">Phone</p>
                  <a href={`tel:${siteConfig.phone.replace(/[^0-9]/g, '')}`} className="font-semibold hover:underline text-sm sm:text-base">{siteConfig.phone}</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--primary-red)]" />
                <div>
                  <p className="text-xs sm:text-sm text-white/60">Email</p>
                  <a href={`mailto:${accessibilityEmail}`} className="font-semibold hover:underline text-sm sm:text-base break-all">{accessibilityEmail}</a>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/20">
              <p className="text-white/60 text-xs sm:text-sm">
                We aim to respond to accessibility feedback within 2 business days.
              </p>
            </div>
          </section>

          {/* Last Updated */}
          <p className="text-center text-gray-500 text-sm mt-8">
            This accessibility statement was last updated on January 2025.
          </p>
        </div>
      </div>
    </div>
  );
}
